import React, { useState } from 'react';
import PageTransition from '../components/ui/PageTransition';
import { motion } from 'framer-motion';
import PricingCard from '../components/ui/PricingCard';
import Analytics from '../services/analytics';
import SignupFormModal from '../components/ui/SignupFormModal';

const frequentlyAskedQuestions = [
  {
    question: 'How many agents can I create on the free plan?',
    answer: 'The free plan allows you to create up to 2 AI agents with basic capabilities. You\'ll have access to core features and limited usage, perfect for trying out the platform or for personal use.'
  },
  {
    question: 'Can I upgrade or downgrade my plan at any time?',
    answer: 'Yes, you can change your plan at any time. When upgrading, you\'ll be charged the prorated amount for the remainder of your billing cycle. When downgrading, the changes will take effect at the start of your next billing cycle.'
  },
  {
    question: 'Is there a limit to the number of users who can access my agents?',
    answer: 'The number of users who can interact with your agents depends on your plan. The Free plan supports up to 5 users, the Pro plan supports up to 25 users, and the Business plan supports unlimited users within your organization.'
  },
  {
    question: 'Do you offer custom enterprise solutions?',
    answer: 'Yes, our Enterprise plan is fully customizable to meet your organization\'s specific needs. Contact our sales team for a consultation on custom pricing, features, and implementation strategies tailored to your business requirements.'
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit cards, including Visa, Mastercard, American Express, and Discover. For annual Enterprise plans, we also offer invoicing and payment by bank transfer.'
  },
  {
    question: 'Is there a setup fee?',
    answer: 'No, there are no setup fees for any of our plans. You only pay the advertised price for your subscription.'
  },
];

const PricingPage: React.FC = () => {
  const [annual, setAnnual] = useState(true);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  
  const pricingPlans = [
    {
      name: 'Free',
      price: annual ? '$0' : '$0',
      description: 'For individuals and small projects getting started with AI agents.',
      features: [
        { text: 'Up to 2 AI agents', included: true },
        { text: 'Basic integrations (Slack & Discord)', included: true },
        { text: '500 interactions per month', included: true },
        { text: 'Community support', included: true },
        { text: 'Advanced AI capabilities', included: false },
        { text: 'Custom integrations', included: false },
        { text: 'Analytics & Reporting', included: false },
        { text: 'Priority support', included: false },
      ],
      buttonText: 'Get Started Free',
    },
    {
      name: 'Pro',
      price: annual ? '$29' : '$39',
      description: 'For professionals and teams who need more power and integrations.',
      features: [
        { text: 'Up to 10 AI agents', included: true },
        { text: 'All standard integrations', included: true },
        { text: '5,000 interactions per month', included: true },
        { text: 'Email & chat support', included: true },
        { text: 'Advanced AI capabilities', included: true },
        { text: 'Custom integrations', included: true },
        { text: 'Basic analytics & reporting', included: true },
        { text: 'Priority support', included: false },
      ],
      popularPlan: true,
      buttonText: 'Start 14-day Free Trial',
    },
    {
      name: 'Business',
      price: annual ? '$99' : '$129',
      description: 'For growing businesses that need advanced features and team collaboration.',
      features: [
        { text: 'Unlimited AI agents', included: true },
        { text: 'All integrations + priority access', included: true },
        { text: '25,000 interactions per month', included: true },
        { text: 'Priority support', included: true },
        { text: 'Advanced AI capabilities', included: true },
        { text: 'Custom integrations', included: true },
        { text: 'Advanced analytics & reporting', included: true },
        { text: 'Team collaboration features', included: true },
      ],
      buttonText: 'Start 14-day Free Trial',
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      description: 'For large organizations with custom needs and high-volume requirements.',
      features: [
        { text: 'Unlimited AI agents', included: true },
        { text: 'Custom & private integrations', included: true },
        { text: 'Unlimited interactions', included: true },
        { text: 'Dedicated account manager', included: true },
        { text: 'Advanced AI capabilities', included: true },
        { text: 'Custom model training', included: true },
        { text: 'Enterprise analytics & reporting', included: true },
        { text: 'SLA & compliance features', included: true },
      ],
      buttonText: 'Contact Sales',
    },
  ];

  const handlePlanSelect = (planName: string) => {
    setSelectedPlan(planName);
    Analytics.trackButtonClick('Select Plan - ' + planName, 'pricing-page');
    // Scroll to the top of the page before showing the modal
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsSignupModalOpen(true);
  };

  const handleTalkToSales = () => {
    Analytics.trackButtonClick('Talk to Sales', 'pricing-page');
    // Scroll to the top of the page before showing the modal
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsSignupModalOpen(true);
  };

  return (
    <>
      <PageTransition>
        <section className="pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <motion.span 
                className="text-primary-600 font-semibold tracking-wide uppercase text-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
              >
                Pricing
              </motion.span>
              <motion.h1 
                className="mt-2 text-4xl sm:text-5xl font-bold text-gray-900"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                Simple, Transparent Pricing
              </motion.h1>
              <motion.p 
                className="mt-4 text-xl text-gray-600"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                Choose the plan that's right for your needs. All plans include a 14-day free trial.
              </motion.p>
              
              <motion.div 
                className="mt-8 flex justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <div className="relative flex bg-gray-100 p-1 rounded-lg">
                  <motion.button
                    className={`relative w-28 py-2 text-sm font-medium rounded-md z-10`}
                    animate={{ 
                      color: !annual ? '#6B7280' : '#FFFFFF'
                    }}
                    transition={{ duration: 0.3 }}
                    whileHover={{ scale: annual ? 1.05 : 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setAnnual(true);
                      Analytics.trackPricingInteraction('toggle', 'annual');
                    }}
                  >
                    Yearly
                    {annual && (
                      <motion.span 
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 5 }}
                        className="absolute text-xs text-green-800 font-semibold -bottom-6 left-0 right-0"
                      >
                        Save 25%
                      </motion.span>
                    )}
                  </motion.button>
                  <motion.button
                    className={`relative w-28 py-2 text-sm font-medium rounded-md z-10`}
                    animate={{ 
                      color: annual ? '#6B7280' : '#FFFFFF'
                    }}
                    transition={{ duration: 0.3 }}
                    whileHover={{ scale: !annual ? 1.05 : 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setAnnual(false);
                      Analytics.trackPricingInteraction('toggle', 'monthly');
                    }}
                  >
                    Monthly
                  </motion.button>
                  <motion.div
                    className="absolute inset-y-1 rounded-md bg-primary-600 z-0"
                    initial={false}
                    animate={{
                      left: annual ? '4px' : 'calc(50% + 4px)'
                    }}
                    transition={{ 
                      type: "spring",
                      stiffness: 300,
                      damping: 30
                    }}
                    style={{
                      width: 'calc(50% - 8px)',
                    }}
                  />
                </div>
              </motion.div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {pricingPlans.map((plan, index) => (
                <PricingCard
                  key={index}
                  name={plan.name}
                  price={plan.price}
                  description={plan.description}
                  features={plan.features}
                  popularPlan={plan.popularPlan}
                  buttonText={plan.buttonText}
                  delay={index * 0.1}
                  onSelect={handlePlanSelect}
                />
              ))}
            </div>
            
            <div className="mt-20">
              <motion.h2 
                className="text-3xl font-bold text-gray-900 text-center mb-12"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
              >
                Frequently Asked Questions
              </motion.h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {frequentlyAskedQuestions.map((faq, index) => (
                  <motion.div 
                    key={index}
                    className="bg-white rounded-lg shadow-sm p-6"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 + 0.1 }}
                  >
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.question}</h3>
                    <p className="text-gray-600">{faq.answer}</p>
                  </motion.div>
                ))}
              </div>
            </div>
            
            <motion.div 
              className="mt-20 text-center bg-gradient-to-r from-primary-600 to-secondary-600 rounded-2xl overflow-hidden shadow-xl"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
            >
              <div className="px-8 py-12 md:py-16 md:px-16 text-center text-white">
                <h3 className="text-2xl md:text-3xl font-bold mb-4">Not sure which plan is right for you?</h3>
                <p className="mb-8 text-lg text-white/80 max-w-2xl mx-auto">
                  Our team is here to help. Schedule a free consultation to discuss your needs and find the perfect plan.
                </p>
                <motion.button 
                  className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold text-lg shadow-md"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleTalkToSales}
                >
                  Talk to Sales
                </motion.button>
              </div>
            </motion.div>
          </div>
        </section>
      </PageTransition>
      
      {/* Signup Modal */}
      <SignupFormModal 
        isOpen={isSignupModalOpen} 
        onClose={() => setIsSignupModalOpen(false)} 
      />
    </>
  );
};

export default PricingPage;